import {
  Input,
  Output,
  OnInit,
  Component,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ChevronLeft, ChevronRight } from './../icon/icon-map';
import { PhxPaginationResponse } from './phx-pagination.types';
import { debounce } from '../../utils';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxNavigationButtonBackground, PhxNavigationButtonSize } from '../navigation-button/phx-navigation-button.types';
import { PHX_BACKGROUND, PHX_SIZE } from '../../constants';

@Component({
  selector: 'phx-pagination',
  styleUrls: ['./phx-pagination.component.scss'],
  templateUrl: './phx-pagination.component.html',
  host: {
    class: 'phx-pagination',
    '[class.phx-pagination-breakpoint-enabled]': 'phxHideContentOnMobile',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxPaginationComponent implements OnInit, OnChanges {
  @Input() phxTotalItems: number = 1;
  @Input() phxPerPageCount: number = 1;
  @Input() phxLoading: boolean = false;
  @Input() phxCurrentPage: number = 1;
  @Input() phxDebounceTime: number = 0;
  @Input() phxHidePagination: boolean = false;
  @Input() phxEnableKeyNavigation: boolean = false;
  @Input() phxSize: PhxNavigationButtonSize = PHX_SIZE.MD;
  @Input() phxBackground: PhxNavigationButtonBackground = PHX_BACKGROUND.STANDARD;
  @Input() phxHideContentOnMobile: boolean = true;
  public debouncedNavigateBackward: () => void;
  public debouncedNavigateForward: () => void;

  @Output() public phxOnNavigate: EventEmitter<PhxPaginationResponse> = new EventEmitter();

  public totalPages: number;
  public readonly ChevronLeftIcon: string = ChevronLeft;
  public readonly ChevronRightIcon: string = ChevronRight;

  constructor(private cdr: ChangeDetectorRef) {
    this.debouncedNavigateBackward = debounce(this.navigateBackward.bind(this), this.phxDebounceTime);
    this.debouncedNavigateForward = debounce(this.navigateForward.bind(this), this.phxDebounceTime);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxDebounceTime, phxTotalItems, phxPerPageCount } = changes;

    if (phxDebounceTime) {
      this.debouncedNavigateBackward = debounce(this.navigateBackward.bind(this), phxDebounceTime.currentValue);
      this.debouncedNavigateForward = debounce(this.navigateForward.bind(this), phxDebounceTime.currentValue);
    }

    if(phxPerPageCount || phxTotalItems) {
      this.calculateTotalPages();
    }
  }

  public ngOnInit(): void {
    this.calculateTotalPages();
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.phxTotalItems / this.phxPerPageCount);
  }

  public navigateBackward(): void {
    if(this.phxCurrentPage === 1) return;
    this.phxCurrentPage -= 1;
    this.cdr.detectChanges();
    const paginationResponse: PhxPaginationResponse = {
      isNextPage: false,
      phxCurrentPage: this.phxCurrentPage,
    };
    this.phxOnNavigate.emit(paginationResponse);
  }

  public navigateForward(): void {
    if(this.phxCurrentPage === this.totalPages) return;
    this.phxCurrentPage += 1;
    this.cdr.detectChanges();
    const paginationResponse: PhxPaginationResponse = {
      isNextPage: true,
      phxCurrentPage: this.phxCurrentPage,
    };
    this.phxOnNavigate.emit(paginationResponse);
  }

  @HostListener('window:keyup', ['$event'])
  public keyEvent(event: KeyboardEvent): void {
    if (this.phxEnableKeyNavigation && !this.phxLoading) {
      if (event.key === KeyboardKeys.ARROW_RIGHT && (this.totalPages && this.phxCurrentPage !== this.totalPages)) this.navigateForward();
      else if (event.key === KeyboardKeys.ARROW_LEFT && (this.phxCurrentPage && this.phxCurrentPage !== 1)) this.navigateBackward();
    }
  }
}
